import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { IRoom } from './iroom';
import { MatPaginator } from '@angular/material/paginator';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, switchMap, takeUntil } from 'rxjs';
import { PaginationResult } from 'src/app/models/common.response';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { RoomService } from './room.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { LookupService } from 'src/app/services/lookup.service';
import { RoomDetailComponent } from './room-detail/room-detail.component';
import { DeleteDialog } from 'src/app/pages/dialogs/delete.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { FloorOrdinalPipe } from 'src/app/pipe/floor.pipe';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';


@Component({
  selector: 'app-room',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    ReactiveFormsModule,
    TranslateModule,
    FloorOrdinalPipe
  ],
  templateUrl: './room.component.html',
  styleUrl: './room.component.scss'
})
export class RoomComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() roomTypeId: number | null = null;
  @Output() roomClick = new EventEmitter<IRoom>();

  displayedColumns: string[] = [
    'roomNumber',
    'floor',
    'roomTypeName',
    'status',
    'isActive',
    'action',
  ];

  dataSource = new MatTableDataSource<IRoom>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);
  private _unsubscribeAll: Subject<void> = new Subject<void>();
  pagination: PaginationResult;
  roomcount: number = 0;
  roomdata: IRoom[];
  isLoading: boolean = false;
  searchRoom: FormControl = new FormControl();
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  roomtypeList: CommonDropDown[];
  selectedRoomTypeName: string = '';

  constructor(
    private _roomService: RoomService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    private _lookupService: LookupService,
  ) { }

  ngOnInit(): void {

    this._roomService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.roomcount = this.pagination.rowCount;
        }
      });

    this._roomService.rooms$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.roomdata = data;
        this.dataSource = new MatTableDataSource(this.roomdata); // Correct initialization
        this.dataSource.paginator = this.paginator;
      });

    this.searchRoom.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isLoading = true;
        const requestPayload: any = {
          pageNumber: 0,
          pageSize: this.pagination.pageSize || 10,
          isDescending: false,
          searchValue: query || ''
        };
        if (this.roomTypeId !== null && this.roomTypeId !== undefined) {
          requestPayload.roomTypeId = this.roomTypeId;
        }
        return this._roomService.getAllRooms(requestPayload).pipe(
          finalize(() => (this.isLoading = false)),
          catchError((error) => {
            this.toastr.error(this.translate.instant('COMMON.ERROR'));
            console.error('Error searching rooms:', error);
            return of(null);
          })
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.roomDetails;
        this.roomcount = response.result.pagination.rowCount;
      }
    });

  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['roomTypeId'] && !changes['roomTypeId'].firstChange) {
      if (this.roomTypeId !== null && this.roomTypeId !== undefined) {
        const roomType = this.roomtypeList?.find(rt => rt.id === this.roomTypeId);
        this.selectedRoomTypeName = roomType ? roomType.name : '';
        this.refreshRoom();
      } else {
        this.selectedRoomTypeName = '';
        this.refreshRoom();
      }
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onAddRoom() {
    this.dialog.open(RoomDetailComponent, {
      width: '90vh',
      height: 'auto',
      disableClose: true,
      data: {
        roomTypeId: this.roomTypeId
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshRoom();
      }
    });
  }

  onUpdateRoom(element: IRoom) {
    this.dialog.open(RoomDetailComponent, {
      width: '90vh',
      height: 'auto',
      disableClose: true,
      data:
      {
        room: element,
        isEdit: true
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshRoom();
      }
    });
  }

  onDeleteRoom(roomId: number) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.ROOM') + ' ?' }
    })
    confirmation.afterClosed().subscribe((result) => {
      if (result === "confirm") {
        this._roomService.deleteRoom(roomId).subscribe((response) => {
          if (response.isSuccess) {
            this.refreshRoom();
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.ROOM')));
          }
          else {
            const allErrors = Object.values(response.errors).flat();
            this.toastr.error(allErrors.join("\n"));
          }
        })
      }
    });

  }

  refreshRoom() {
    const globalSearchValue = this.searchRoom?.value?.trim();
    const requestPayload: any = {
      pageNumber: 0,
      pageSize: this.pagination?.pageSize || 100,
      isDescending: false
    };

    if (globalSearchValue) {
      requestPayload.SearchValue = globalSearchValue;
    } else {
      requestPayload.pageNumber = this.pagination?.currentPage || 0;
    }

    if (this.roomTypeId !== null && this.roomTypeId !== undefined) {
      requestPayload.roomTypeId = this.roomTypeId;
    }

    this._roomService.getAllRooms(requestPayload)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          this.toastr.error(this.translate.instant('COMMON.ERROR'));
          console.error('Error refreshing rooms:', error);
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response && response.result) {
          const RoomType = response.result.roomDetails;
          this.roomdata = RoomType;
          if (!this.dataSource) {
            this.dataSource = new MatTableDataSource(RoomType);
          } else {
            this.dataSource.data = RoomType;
          }
          this.dataSource.paginator = this.paginator;
          this.roomcount = response.result.pagination.rowCount;
          this.pagination = response.result.pagination;
        }
      });

  }

  onRowClick(room: IRoom): void {
    this.roomClick.emit(room);
  }

  onActionClick(event: Event): void {
    event.stopPropagation();
  }
}
